import { useMemo } from 'react';
import { ChatMessage, Client } from '../../types';
import { Avatar, Badge } from '@nextui-org/react';
import { useAppSelector } from '../../store/hooks';
import {
  riskToLabel,
  selectRiskOfChats,
} from '../../store/assistant/assistantSlice';
import { selectActorConfig } from '../../store/settings/settingsSlice';
import { getStringBetween } from '../../util';

export interface ChatListItemProps {
  client: Client;
  messages?: { [messageId: string]: ChatMessage };
  showAvatar?: boolean;
}

export default function ChatListItem({
  client,
  messages,
  showAvatar,
}: ChatListItemProps) {
  const riskOfChats = useAppSelector(selectRiskOfChats);
  const actorConfig = useAppSelector(selectActorConfig);
  const selectedActorDetails = useMemo(
    () => actorConfig?.actorDetails,
    [actorConfig]
  );

  const unreadCount = useMemo(() => {
    let count = 0;
    if (!messages) {
      return count;
    }
    for (const messageId in messages) {
      if (messages[messageId].isUnread) {
        count += 1;
      }
    }
    return count;
  }, [messages]);

  const lastMessage = useMemo(() => {
    if (!messages) {
      return;
    }
    let message: ChatMessage | null = null;
    for (const messageId in messages) {
      if (!message) {
        message = messages[messageId];
        continue;
      }
      if (messages[messageId].createdAt.localeCompare(message.createdAt) > 0) {
        message = messages[messageId];
      }
    }

    if (client.userType === 'actor') {
      return !selectedActorDetails
        ? ''
        : `${message?.content} (${client.level})`;
    }

    const match = message?.content.match(/(?<=<)\d+(\.\d+)?(?=>)/);
    const numberInBrackets = match ? match[0] : null;
    let content = message?.content;

    if (numberInBrackets) {
      content = message?.content.replace(`<${numberInBrackets}>`, '');
    }

    const response = content
      ? getStringBetween(content, '<<RESPONSE>>', '<</RESPONSE>>')
      : content;

    return response ? response : content;

    // return message?.content;
  }, [messages, client, selectedActorDetails]);

  const displayName = useMemo(() => {
    if (client.userType === 'actor') {
      return client.name || client.username || client.userId;
    }

    return client.friendlyName || client.username || client.userId;
  }, [client]);

  const riskLevel = useMemo(
    () => riskOfChats[client.userId],
    [client, riskOfChats]
  );

  const avatarUri = useMemo(
    () => `${process.env.PUBLIC_URL}/avatars/${client.userId}.png`,
    [client]
  );

  return (
    <div className="grid grid-rows-1 grid-cols-4 gap-1">
      <div className="col-span-1 flex justify-start items-center">
        <Badge
          content={unreadCount}
          isInvisible={unreadCount === 0}
          color="primary"
          shape="circle"
          placement="bottom-right"
          className="mb-1"
        >
          <Badge
            content=""
            shape="circle"
            variant="shadow"
            color={
              riskLevel
                ? riskLevel.risk
                  ? riskToLabel(riskLevel.risk).color
                  : undefined
                : undefined
            }
            isInvisible={!riskLevel}
            className="mt-1"
          >
            <Avatar
              size="md"
              showFallback
              src={showAvatar ? avatarUri : undefined}
              name={displayName
                .split(/\s+/)
                .filter((s) => !!s)
                .map((s) => s[0])
                .slice(0, 2)
                .join('')}
              className="border-1 border-primary-900"
            />
          </Badge>
        </Badge>
      </div>
      <div className="col-span-3 flex flex-col justify-center min-w-0">
        <p className="text-base font-semibold text-ellipsis overflow-hidden">
          {displayName}
        </p>
        {lastMessage && (
          <p className="text-xs text-ellipsis overflow-hidden opacity-70 two-lines">
            {lastMessage}
          </p>
        )}
      </div>
    </div>
  );
}
